import {
  ApiError,
  type FanResponseDto,
  type OnlyFansMessage,
  VaultMediaType,
} from '@api'
import { type AxiosError, isAxiosError } from 'axios'
import { differenceInDays } from 'date-fns/differenceInDays'
import { differenceInHours } from 'date-fns/differenceInHours'
import { differenceInMinutes } from 'date-fns/differenceInMinutes'
import { format } from 'date-fns/format'
import { isAfter } from 'date-fns/isAfter'
import { isSameYear } from 'date-fns/isSameYear'
import { isToday } from 'date-fns/isToday'
import { subHours } from 'date-fns/subHours'

import {
  MediaItem,
  UploadingMediaItem,
} from '@/models/chattingUI/messageEditor/medias/types'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { formatNumberWithPoints } from '@/utils/helpers'
import { getSubcriptionState } from '@/utils/subscription.ts'
import { formatDate, utcToLocalDate } from '@/utils/time'

import FlameIcon from '../icons/flame.svg'
import NoIncomeIcon from '../icons/no-income.svg'
import type {
  Chat,
  CloneMediaJobResponse,
  MediaAttachment,
  UploadedFileDetails,
} from '../types'

export function getTimeAgo(date: Date): { count: number; unit: string } {
  const now: Date = new Date()
  const elapsedTime: number = now.getTime() - date.getTime()
  const seconds: number = Math.floor(elapsedTime / 1000)
  const minutes: number = Math.floor(seconds / 60)
  const hours: number = Math.floor(minutes / 60)
  const days: number = Math.floor(hours / 24)

  if (days > 0) {
    return { count: days, unit: days === 1 ? 'day' : 'days' }
  } else if (hours > 0) {
    return { count: hours, unit: hours === 1 ? 'hour' : 'hours' }
  } else if (minutes > 0) {
    return { count: minutes, unit: minutes === 1 ? 'minute' : 'minutes' }
  } else {
    return { count: seconds, unit: seconds === 1 ? 'second' : 'seconds' }
  }
}

const compactNumberFormatter = Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
  maximumSignificantDigits: 3,
})

export const formatNumber = (number: number) => {
  if (number < 1000) {
    return formatNumberWithPoints(number)
  }

  return compactNumberFormatter.format(number)
}

export const getSubscriptionTagConfig = (details: FanResponseDto) => {
  const subscription = details.subscribedOnData

  if (!subscription) return null

  const { isExpired, isSubscribed } = getSubcriptionState(subscription)
  const isRenewOn = hasRebillOn(details)

  if (!isSubscribed && !isExpired) return null

  if (isExpired) {
    return {
      tooltip: 'Expired Fan and Renew Off',
      bgClassName: 'text-danger-default',
    }
  }

  if (isSubscribed && isRenewOn) {
    return {
      tooltip: 'Subscription and Renew On',
      bgClassName: 'text-success-default',
    }
  }

  if (isSubscribed && !isRenewOn) {
    return {
      tooltip: 'Subscription On and Renew Off',
      bgClassName: 'text-warning-dark',
    }
  }
}

export const getSpendingConfig = (
  lastPPVPurchase?: FanResponseDto['lastPPVPurchase'],
) => {
  if (!lastPPVPurchase) {
    return null
  }

  const localDate = utcToLocalDate(new Date(lastPPVPurchase))
  const time = localDate.getTime()

  const { count, unit } = getTimeAgo(localDate)
  const text = `${count}${unit.slice(0, 1).toLowerCase()}`

  if (isAfter(time, subHours(new Date(), 12))) {
    return {
      tooltip: `Last Sale - ${count} ${unit} ago`,
      Icon: FlameIcon,
      bgClassName: 'bg-primary-500',
      text,
    }
  } else {
    return {
      tooltip: `Not Spending - since ${formatDate(localDate)}`,
      Icon: NoIncomeIcon,
      bgClassName: 'bg-danger-default',
      text,
    }
  }
}

export function secondsToTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  const formattedMinutes = String(minutes).padStart(2, '0')
  const formattedSeconds = String(remainingSeconds).padStart(2, '0')

  return `${formattedMinutes}:${formattedSeconds}`
}

export const getTimeDiffFromNow = (date: Date) => {
  const now = new Date()

  const diffInMinutes = differenceInMinutes(now, date)
  const diffInHours = differenceInHours(now, date)
  const diffInDays = differenceInDays(now, date)

  const isFuture = isAfter(date, now)

  if (isFuture) {
    if (diffInMinutes > -1) {
      return 'just now'
    }

    if (diffInMinutes > -60) {
      return `${Math.abs(diffInMinutes)} minutes later`
    }

    if (diffInHours > -24) {
      return `${Math.abs(diffInHours)} hours later`
    }

    if (diffInDays > -1) {
      return 'Tomorrow'
    }

    return format(date, 'MMM d, yyyy')
  }

  if (diffInMinutes < 1) {
    return 'just now'
  }

  if (diffInHours < 1) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`
  }

  if (isToday(date) && diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`
  }
  if (diffInDays <= 1) {
    return 'Yesterday'
  }
  if (diffInDays < 7) {
    return format(date, 'EEEE')
  }
  if (isSameYear(now, date)) {
    return format(date, 'MMM d • hh:mm a')
  }

  return format(date, 'MMM d, yyyy')
}

export function toDateFormat(date: string) {
  try {
    return format(new Date(date.split('T')[0] + 'T00:00:00'), 'MM/dd/yyyy')
  } catch (error) {
    return 'Not set'
  }
}

export function isUploadedFile(
  item: UploadedFileDetails | StreamableVaultMedia,
): item is UploadedFileDetails {
  return 'name' in item && 'processId' in item && 'host' in item
}

export const getIsChatRestricted = ({
  canSendMessage,
  canNotSendReason,
}: Chat) => {
  return !canSendMessage && ['restricted', 'muted'].includes(canNotSendReason!)
}

export const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Returns media type to show an icon in message preview
 */
export function getMessageMediaPreviewType(
  message: OnlyFansMessage,
): VaultMediaType | null {
  const lastMedia = message.media?.[message.media?.length - 1]

  if (lastMedia) {
    return lastMedia.type as VaultMediaType
  }

  if (message.giphyId) {
    return VaultMediaType.GIF
  }

  if (message.mediaCount) {
    return VaultMediaType.PHOTO
  }

  return null
}

export function extractFanName(fan: FanResponseDto) {
  return fan.displayName || fan.name || fan.username || 'Fan'
}

// https://github.com/creatorheroapp/onlyfans-source/blob/code-prod/remapped/of-vue/src/components/Subscribers/Item.vue
export function hasRebillOn(fan: FanResponseDto) {
  return (
    !!fan.subscribedOnData &&
    (!fan.subscribedOnData.subscribePrice || !!fan.subscribedOnData.renewedAt)
  )
}

export const checkSendDisabled = ({
  canBeSent,
  uploadingMedias,
  text,
  medias,
  attachedAudio,
  giphyId,
  isTranslating,
  isLoadingScriptMedias,
  cloneMediaJobsInProgress,
  price,
}: {
  canBeSent?: boolean
  uploadingMedias?: UploadingMediaItem[]
  text?: string
  medias?: MediaItem[]
  attachedAudio?: MediaAttachment
  giphyId?: string | null
  isTranslating?: boolean
  isLoadingScriptMedias?: boolean
  cloneMediaJobsInProgress?: CloneMediaJobResponse[]
  price?: number
}) => {
  if (price && !medias?.some(({ isPreview }) => !isPreview)) {
    return {
      isDisabled: true,
      reason: 'Please add paid media attachments!',
      isLoading: false,
    }
  }

  // need to check explicit `false`
  if (canBeSent === false) {
    return { isDisabled: true, reason: 'No fans selected', isLoading: false }
  }
  if (uploadingMedias?.length) {
    return { isDisabled: true, reason: 'Uploading media...', isLoading: true }
  }
  if (isTranslating) {
    return {
      isDisabled: true,
      reason: 'Translating your message...',
      isLoading: true,
    }
  }
  if (isLoadingScriptMedias) {
    return {
      isDisabled: true,
      reason: 'Loading script media...',
      isLoading: true,
    }
  }
  if (cloneMediaJobsInProgress?.length) {
    return { isDisabled: true, reason: 'Cloning media...', isLoading: true }
  }
  if (!text?.trim() && !medias?.length && !attachedAudio && !giphyId) {
    return { isDisabled: true, reason: 'Add text or media', isLoading: false }
  }

  return { isDisabled: false, reason: '', isLoading: false }
}
